import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {
  AiActionStatus,
  AiMessageRole,
  AiMessageView,
  NameCandidate,
  NameMention,
  SendAiMessageResponse,
} from '../../../../../api';
import {AiChatService} from '../../services/ai-chat.service';
import {AiStepService} from '../../services/ai-step.service';
import {AiVoiceService} from '../../services/ai-voice.service';
import {AiMarkdownPipe} from '../../pipes/ai-markdown.pipe';
import {AiActionCard} from './components/ai-action-card/ai-action-card';
import {AiNamePicker} from './components/ai-name-picker/ai-name-picker';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {TextInput} from '../../../../components/text-input/text-input';

@Component({
  selector: 'elcia-ai-chat',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, AiActionCard, AiNamePicker, ElciaButton, TextInput, AiMarkdownPipe],
  providers: [AiChatService, AiStepService, AiVoiceService],
  templateUrl: './ai-chat.html',
  styleUrl: './ai-chat.scss',
})
export class AiChat implements OnDestroy {

  private readonly chatService = inject(AiChatService);
  private readonly stepService = inject(AiStepService);
  private readonly voiceService = inject(AiVoiceService);

  protected readonly isRecording = this.voiceService.isRecording;
  protected readonly isMicUnavailable = this.voiceService.isUnavailable;
  protected readonly isTranscribing = computed(() => this.chatService.transcribeMutation.isPending());

  protected readonly elapsedLabel = computed(() => {
    const seconds = this.voiceService.elapsedSeconds();

    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  });

  /** Outils déjà appelés pour la question en cours, affichés le temps de l'attente. */
  protected readonly steps = this.stepService.steps;

  protected readonly draft = new FormControl<string | null>('');

  protected readonly messages = signal<AiMessageView[]>([]);

  // Terme ambigu en attente d'un choix. Tant qu'il est là, rien n'est envoyé au modèle.
  protected readonly pendingMention = signal<NameMention | null>(null);
  protected readonly hasFailed = signal(false);
  protected readonly hasDictationFailed = signal(false);

  protected readonly isThinking = computed(() =>
    this.chatService.sendMessageMutation.isPending() || this.chatService.startConversationMutation.isPending());
  protected readonly isResolvingAction = computed(() =>
    this.chatService.confirmActionMutation.isPending() || this.chatService.cancelActionMutation.isPending());

  // Un seul garde pour Entrée et pour le bouton : sinon Entrée envoie pendant une dictée.
  protected readonly isBusy = computed(() => this.isThinking() || this.isRecording() || this.isTranscribing());

  protected readonly userRole = AiMessageRole.USER;

  private conversationId: number | null = null;
  private conversationRequest: Promise<number> | null = null;
  private lastQuestion = '';

  // Les messages ajoutés localement n'ont pas encore d'identifiant serveur : on leur en donne un
  // négatif, décroissant, pour que le suivi de liste reste stable sans collision.
  private nextLocalId = -1;

  // Position de lecture relevée avant le rendu du nouveau message : après, la mesure est faussée.
  private isThreadAtBottom = true;

  private readonly thread = viewChild<ElementRef<HTMLDivElement>>('thread');

  // Le fil descend sur le dernier message. Sans ça, une carte de proposition arrivant après
  // quelques échanges reste sous la ligne de flottaison : on voit son titre, pas ses boutons.
  // Mais si l'utilisateur est remonté relire, on lui laisse sa place.
  private readonly followThread = afterRenderEffect(() => {
    this.messages();
    this.pendingMention();
    this.isThinking();
    this.steps();

    const element = this.thread()?.nativeElement;
    if (element && this.isThreadAtBottom) { element.scrollTop = element.scrollHeight; }
  });

  // Toutes les façons de fermer le panneau passent ici : croix, Échap, clic hors du dialogue.
  ngOnDestroy(): void {
    if (this.conversationId == null) return;

    this.chatService.discardConversation(this.conversationId);
    this.conversationId = null;
  }

  protected async send(): Promise<void> {
    const text = (this.draft.value ?? '').trim();
    if (!text || this.isBusy()) return;

    // Poser une nouvelle question abandonne la désambiguïsation en attente : sinon choose()
    // rejouerait lastQuestion, désormais écrasée par celle-ci.
    this.pendingMention.set(null);

    this.draft.setValue('');
    this.append({
      id: this.nextLocalId--,
      role: AiMessageRole.USER,
      content: text,
      createdAt: new Date(),
    } as AiMessageView);

    await this.ask(text);
  }

  /** Rejoue la dernière question après un échec, sans faire retaper le texte. */
  protected async retry(): Promise<void> {
    if (!this.lastQuestion || this.isBusy()) return;

    await this.ask(this.lastQuestion);
  }

  // Relevé pendant que l'utilisateur fait défiler : après le rendu d'un nouveau message, la distance
  // au bas inclut déjà ce message et ne dit plus où il en était.
  protected onThreadScroll(): void {
    const element = this.thread()?.nativeElement;
    if (!element) return;

    this.isThreadAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 40;
  }

  /**
   * La dictée remplit la zone de saisie, elle n'envoie pas : un nom mal transcrit se corrige
   * avant que le modèle ne le voie, et c'est là notre meilleure défense contre la transcription
   * approximative des noms propres.
   */
  protected async toggleRecording(): Promise<void> {
    if (!this.isRecording()) {
      await this.voiceService.start();
      return;
    }

    const recording = await this.voiceService.stop();
    if (!recording) return;

    this.hasDictationFailed.set(false);

    try {
      const response = await this.chatService.transcribeMutation.mutateAsync(recording);
      const text = response.text?.trim();
      if (!text) return;

      const current = (this.draft.value ?? '').trim();
      this.draft.setValue(current ? `${current} ${text}` : text);
    } catch {
      // Sans ce message, une dictée perdue ressemble à un micro qui n'a rien capté.
      this.hasDictationFailed.set(true);
    }
  }

  protected async choose(candidate: NameCandidate): Promise<void> {
    const mention = this.pendingMention();
    if (!mention || this.conversationId == null) return;

    this.pendingMention.set(null);

    try {
      await this.chatService.resolveNameMutation.mutateAsync({
        conversationId: this.conversationId,
        body: {
          term: mention.term,
          entityType: candidate.type,
          entityId: candidate.id,
        },
      });
    } catch {
      // Le choix n'a pas été enregistré : on remet le sélecteur, sinon il ne reste rien à cliquer.
      this.pendingMention.set(mention);
      this.hasFailed.set(true);

      return;
    }

    // Le choix est enregistré pour toute la conversation : on rejoue la question telle quelle.
    await this.ask(this.lastQuestion);
  }

  protected async confirm(message: AiMessageView): Promise<void> {
    try {
      await this.chatService.confirmActionMutation.mutateAsync(message.id);
      this.resolveAction(message.id, AiActionStatus.CONFIRMED);
    } catch {
      // La carte reste en attente : l'utilisateur peut recliquer, rien n'a été exécuté.
      this.hasFailed.set(true);
    }
  }

  protected async cancel(message: AiMessageView): Promise<void> {
    try {
      await this.chatService.cancelActionMutation.mutateAsync(message.id);
      this.resolveAction(message.id, AiActionStatus.CANCELLED);
    } catch {
      this.hasFailed.set(true);
    }
  }

  private async ask(text: string): Promise<void> {
    this.lastQuestion = text;
    this.hasFailed.set(false);
    this.stepService.reset();

    try {
      const conversationId = await this.ensureConversationAsync();

      // Les étapes arrivent par le hub : sans cette conversation, un autre onglet du même
      // utilisateur verrait ses propres outils s'afficher ici.
      this.stepService.watch(conversationId);

      const response: SendAiMessageResponse = await this.chatService.sendMessageMutation.mutateAsync({
        conversationId,
        body: {text},
      });

      if (response.disambiguations.length > 0) {
        this.pendingMention.set(response.disambiguations[0]);
        return;
      }

      this.messages.update(messages => [...messages, ...response.messages]);
      this.stepService.reset();
    } catch {
      // Les intercepteurs affichent déjà le toast d'erreur ; ici on garde juste le fil lisible.
      this.hasFailed.set(true);
    }
  }

  // La conversation n'est créée qu'au premier message : ouvrir puis fermer le panneau n'en crée aucune.
  // Deux envois rapprochés partagent la même création : sans cela, le second ouvrirait une conversation
  // parallèle — contexte scindé, et la première jamais fermée côté serveur.
  private async ensureConversationAsync(): Promise<number> {
    if (this.conversationId != null) return this.conversationId;

    this.conversationRequest ??= this.chatService.startConversationMutation
      .mutateAsync()
      .then(response => response.conversationId);

    try {
      this.conversationId = await this.conversationRequest;

      return this.conversationId;
    } finally {
      // Remis à zéro même en cas d'échec : la question suivante doit pouvoir retenter.
      this.conversationRequest = null;
    }
  }

  private resolveAction(messageId: number, status: AiActionStatus): void {
    this.messages.update(messages => messages.map(message =>
      message.id === messageId ? {...message, actionStatus: status} : message));
  }

  private append(message: AiMessageView): void {
    this.messages.update(messages => [...messages, message]);
  }
}
